/**
 * ImageKit Upload Utilities
 *
 * Provides browser-based file upload functionality to ImageKit with automatic image compression.
 *
 * @example Basic file validation
 * ```typescript
 * try {
 *   validateFile(file, {
 *     allowedMime: ["image/jpeg", "image/png", "application/pdf"],
 *     maxBytes: 25 * 1024 * 1024, // 25MB
 *   });
 * } catch (error) {
 *   if (error.code === "FILE_TOO_LARGE") {
 *     console.error(`Max size: ${error.maxBytes} bytes`);
 *   }
 * }
 * ```
 *
 * @example Image compression
 * ```typescript
 * const compressedFile = await maybeCompressImage(file);
 * ```
 *
 * @example Direct upload to ImageKit
 * ```typescript
 * const result = await uploadToImageKit(file, {
 *   folder: "/uploads/avatars",
 *   fileName: "profile-pic.jpg",
 * });
 * console.log(result.url);
 * ```
 *
 * @example Complete upload flow
 * ```typescript
 * try {
 *   validateFile(file, { allowedMime: ["image/jpeg"], maxBytes: 10485760 });
 *   const compressed = await maybeCompressImage(file);
 *   const { url } = await uploadToImageKit(compressed, { folder: "/uploads" });
 *   console.log("Uploaded:", url);
 * } catch (error) {
 *   console.error("Upload failed:", error);
 * }
 * ```
 *
 * Environment variables required:
 * - NEXT_PUBLIC_IMAGEKIT_URL_ENDPOINT: ImageKit URL endpoint
 * - NEXT_PUBLIC_IMAGEKIT_PUBLIC_KEY: ImageKit public key
 * - IMAGEKIT_PRIVATE_KEY: ImageKit private key (server-side only)
 */

import imageCompression from "browser-image-compression";
import ImageKit from "imagekit-javascript";

import { env } from "@/env";
import { getRequestAuthToken } from "@/utils/auth";

const MAX_IMAGE_WIDTH = 2560;
const MAX_FILE_SIZE_MB = 1;
const IMAGE_QUALITY = 0.8;
const COMPRESSION_THRESHOLD_BYTES = 500 * 1024;

const MIME_TYPES = {
  IMAGE_PREFIX: "image/",
  PDF: "application/pdf",
} as const;

const ERROR_CODES = {
  NO_FILE: "NO_FILE",
  FILE_TOO_LARGE: "FILE_TOO_LARGE",
  UNSUPPORTED_TYPE: "UNSUPPORTED_TYPE",
  UPLOAD_FAILED: "UPLOAD_FAILED",
} as const;

const API_ENDPOINTS = {
  IMAGEKIT_AUTH: "/billing/calculate-license-payments/imagekit-upload-auth",
} as const;

const ERROR_MESSAGES = {
  AUTH_FAILED: "Failed to get authentication parameters",
} as const;

type ValidationConfig = {
  allowedMime: string[];
  maxBytes: number;
};

type ValidationError =
  | { code: typeof ERROR_CODES.NO_FILE }
  | { code: typeof ERROR_CODES.FILE_TOO_LARGE; maxBytes: number }
  | { code: typeof ERROR_CODES.UNSUPPORTED_TYPE; allowed: string[] };

/**
 * Validates a file against size and MIME type constraints.
 *
 * @param file - The file to validate
 * @param cfg - Validation configuration
 * @param cfg.allowedMime - Array of allowed MIME types
 * @param cfg.maxBytes - Maximum file size in bytes
 *
 * @throws {ValidationError} When validation fails
 * - NO_FILE: File is null or undefined
 * - FILE_TOO_LARGE: File exceeds maxBytes
 * - UNSUPPORTED_TYPE: File MIME type not in allowedMime
 *
 * @example
 * ```typescript
 * validateFile(file, {
 *   allowedMime: ["image/jpeg", "image/png"],
 *   maxBytes: 5 * 1024 * 1024,
 * });
 * ```
 */
export const validateFile = (file: File | null | undefined, cfg: ValidationConfig): void => {
  if (!file) {
    throw { code: ERROR_CODES.NO_FILE } as ValidationError;
  }

  if (file.size > cfg.maxBytes) {
    throw {
      code: ERROR_CODES.FILE_TOO_LARGE,
      maxBytes: cfg.maxBytes,
    } as ValidationError;
  }

  if (!cfg.allowedMime.includes(file.type)) {
    throw {
      code: ERROR_CODES.UNSUPPORTED_TYPE,
      allowed: cfg.allowedMime,
    } as ValidationError;
  }
};

const compressImageFile = async (file: File): Promise<File> => {
  const options = {
    maxWidthOrHeight: MAX_IMAGE_WIDTH,
    maxSizeMB: MAX_FILE_SIZE_MB,
    initialQuality: IMAGE_QUALITY,
    useWebWorker: true,
    fileType: "image/webp",
  };

  try {
    return await imageCompression(file, options);
  } catch {
    return file;
  }
};

const shouldCompressFile = (file: File): boolean => {
  const isImage = file.type.startsWith(MIME_TYPES.IMAGE_PREFIX);
  const isLargeEnough = file.size > COMPRESSION_THRESHOLD_BYTES;
  return isImage && isLargeEnough;
};

/**
 * Compresses image files to optimize size before upload.
 *
 * Uses browser-image-compression library to:
 * - Downscale images wider than 2560px
 * - Convert to WebP format
 * - Target max 1MB file size
 * - Use WebWorkers for non-blocking compression
 *
 * Compression is only applied to images larger than 500KB to avoid
 * increasing file size of already optimized images.
 *
 * Non-image files and small images are returned unchanged.
 *
 * @param file - The file to compress
 * @returns Compressed file or original if compression not needed, fails, or not an image
 *
 * @example
 * ```typescript
 * const largeFile = new File([blob], "photo.jpg", { type: "image/jpeg" }); // 2MB
 * const compressedFile = await maybeCompressImage(largeFile);
 * console.log(`Original: ${largeFile.size}B, Compressed: ${compressedFile.size}B`);
 *
 * const smallFile = new File([blob], "icon.png", { type: "image/png" }); // 100KB
 * const result = await maybeCompressImage(smallFile);
 * console.log(result === smallFile); // true - not compressed
 * ```
 */
export const maybeCompressImage = async (file: File): Promise<File> => {
  if (!shouldCompressFile(file)) {
    return file;
  }

  return compressImageFile(file);
};

/** Ensures filename extension matches WebP MIME after compression. */
export const normalizeCompressedImageFile = (croppedFile: File, processed: File): File => {
  if (processed.type === "image/webp" && !/\.webp$/i.test(processed.name)) {
    const base = croppedFile.name.replace(/\.[^.]+$/, "");
    return new File([processed], `${base}.webp`, { type: "image/webp" });
  }
  return processed;
};

const DEFAULT_UPLOAD_FOLDER = "/uploads";

type UploadOptions = {
  folder?: string;
  fileName?: string;
  useUniqueFileName?: boolean;
};

type UploadResult = {
  url: string;
  fileId: string;
  size: number;
  mime: string;
  originalName: string;
};

type AuthResponse = {
  token: string;
  expire: number;
  signature: string;
};

type UploadError = {
  code: typeof ERROR_CODES.UPLOAD_FAILED;
  error: unknown;
};

const createImageKitInstance = (): ImageKit | null => {
  const urlEndpoint = env.imageKitUrl;
  const publicKey = env.imageKitPublicKey;

  return new ImageKit({
    urlEndpoint,
    publicKey,
  });
};

const imagekitInstance = createImageKitInstance();

export const getImageKitRenditionUrl = (
  src: string | null,
  dimensions: { width: number; height: number }
): string | null => {
  if (!src) return null;
  if (!imagekitInstance) return src;

  return imagekitInstance.url({
    src,
    transformationPosition: "query",
    transformation: [
      {
        width: dimensions.width,
        height: dimensions.height,
        crop: "at_max",
        quality: 80,
      },
    ],
  });
};

const fetchAuthParams = async (): Promise<AuthResponse> => {
  const baseUrl = env.backendUrl.replace(/\/graphql\/?$/, "");
  const response = await fetch(`${baseUrl}${API_ENDPOINTS.IMAGEKIT_AUTH}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${(await getRequestAuthToken()) ?? ""}`,
    },
  });

  if (!response.ok) {
    throw new Error(ERROR_MESSAGES.AUTH_FAILED);
  }

  return response.json();
};

const createUploadResult = (
  result: {
    url: string;
    fileId: string;
    size: number;
    fileType: string;
  },
  originalFileName: string
): UploadResult => {
  return {
    url: result.url,
    fileId: result.fileId,
    size: result.size,
    mime: result.fileType,
    originalName: originalFileName,
  };
};

const uploadFileToImageKit = (
  file: File,
  authParams: AuthResponse,
  opts?: UploadOptions
): Promise<UploadResult> => {
  if (!imagekitInstance) {
    return Promise.reject({
      code: ERROR_CODES.UPLOAD_FAILED,
      error: "ImageKit not configured. Missing environment variables.",
    });
  }

  const folder = opts?.folder ? `${DEFAULT_UPLOAD_FOLDER}${opts.folder}` : DEFAULT_UPLOAD_FOLDER;
  const useUniqueFileName = opts?.useUniqueFileName ?? true;

  return new Promise((resolve, reject) => {
    imagekitInstance.upload(
      {
        file,
        fileName: opts?.fileName || file.name,
        useUniqueFileName,
        folder,
        signature: authParams.signature,
        token: authParams.token,
        expire: authParams.expire,
      },
      (err, result) => {
        if (err) {
          const uploadError: UploadError = {
            code: ERROR_CODES.UPLOAD_FAILED,
            error: err,
          };
          reject(uploadError);
          return;
        }

        if (!result) {
          const uploadError: UploadError = {
            code: ERROR_CODES.UPLOAD_FAILED,
            error: "No result",
          };
          reject(uploadError);
          return;
        }

        resolve(createUploadResult(result, file.name));
      }
    );
  });
};

/**
 * Uploads a file directly to ImageKit from the browser.
 *
 * Handles authentication automatically by fetching signature from server.
 * All files are uploaded to /uploads/ folder by default.
 *
 * @param file - The file to upload
 * @param opts - Upload options
 * @param opts.folder - Additional folder path (e.g., "/users/123/avatars") - will be prefixed with /uploads/
 * @param opts.fileName - Custom filename (default: original file name)
 * @param opts.useUniqueFileName - Add unique suffix to filename (default: true). Set to false to replace existing files.
 *
 * @returns Upload result containing URL and metadata
 *
 * @throws {UploadError} When upload fails
 * - UPLOAD_FAILED: Upload request failed or no result returned
 *
 * @example Avatar upload (replaces existing)
 * ```typescript
 * const result = await uploadToImageKit(file, {
 *   folder: "/users/123/avatars",
 *   fileName: "avatar.jpg",
 *   useUniqueFileName: false,
 * });
 * // Result: /uploads/users/123/avatars/avatar.jpg (replaces if exists)
 * ```
 *
 * @example Document upload (unique files)
 * ```typescript
 * const result = await uploadToImageKit(file, {
 *   folder: "/tournaments/456/documents",
 * });
 * // Result: /uploads/tournaments/456/documents/document_abc123.pdf (unique)
 * ```
 *
 * @example With error handling
 * ```typescript
 * try {
 *   const { url } = await uploadToImageKit(file);
 *   console.log("Uploaded to:", url);
 * } catch (error) {
 *   if (error.code === "UPLOAD_FAILED") {
 *     console.error("Upload failed:", error.error);
 *   }
 * }
 * ```
 */
export const uploadToImageKit = async (file: File, opts?: UploadOptions): Promise<UploadResult> => {
  const authParams = await fetchAuthParams();
  return uploadFileToImageKit(file, authParams, opts);
};
